/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Swal from 'sweetalert2';

// @mui material components
import Icon from '@mui/material/Icon';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';

// Material Dashboard 2 PRO React examples
import { useTranslation } from 'react-i18next';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import { useSelector } from 'react-redux';
import * as Func from 'services/Function';
import TagProduct from './TagProduct';
import TagProductLocation from './TagProductLocation';
import './Tag.css';

const initParent = {
  id: '',
  code: '',
  name: '',
  branch_id: '',
  enable: 'Y',
  created_at: '',
  updated_at: '',
};

const initAttr = {
  id: '',
  line: '',
  line1: '',
  line2: '',
  line3: '',
  name: '',
  barcode1_id: '',
  barcode2_id: '',
  barcode3_id: '',
  enable: 'Y',
  created_at: '',
  updated_at: '',
};

const initFind = {
  search: '',
  order_by: 'tb.id',
  order_sort: 'asc',
  page: 1,
  perPage: 10,
  pageTotal: 0,
};

function TagList() {
  const mod = 'tag_list';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [parent, setParent] = useState(initParent);
  const [record, setRecord] = useState([]);
  const [attr, setAttr] = useState(initAttr);
  const [find, setFind] = useState(initFind);
  const [notify, setNotify] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalEdit, setModalEdit] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [cboCampaign, setCboCampaign] = useState([]);

  const showNotify = (msg, color) => {
    setNotify(
      <Snackbar
        open
        autoHideDuration={3000}
        onClose={() => setNotify(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setNotify(null)} severity={color} variant='filled'>
          {msg}
        </Alert>
      </Snackbar>
    );
  };

  const view = (data) => {
    setFind(data);
    setLoading(true);

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
      ref_id: id,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);
        setParent(result.parent);
      }
      setLoading(false);
    });
  };

  const add = (item) => {
    setLoading(true);

    FetchData('PUT', `/${mod}`, app.token, {
      tag_id: id,
      product_id: item.product_id,
      name: `${item.code} ${item.name}`,
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        view(find);
        showNotify(t(`alert.addcomplete`), 'success');
      } else {
        showNotify(result.msg, 'error');
      }
    });
  };

  const open = (item) => {
    setLoading(true);
    setConfirm(false);

    FetchData('GET', `/${mod}/${item.id}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setCboCampaign(result.campaign);
        setAttr(result.data);
        setModalEdit(true);

        setLoading(false);
      }
    });
  };

  const editConfirm = () => {
    if (!Func.verifyLength(attr.name, 1)) return;
    if (!Func.verifyLength(attr.line1, 1)) return;

    setConfirm(true);
  };

  const edit = () => {
    setLoading(true);
    setConfirm(false);
    setModalEdit(false);

    FetchData('PATCH', `/${mod}/${attr.id}`, app.token, {
      name: attr.name,
      line: attr.line,
      line1: attr.line1,
      line2: attr.line2,
      line3: attr.line3,
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        view(find);
        showNotify(t(`alert.editcomplete`), 'success');
      } else {
        setModalEdit(true);
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const del = () => {
    setModalEdit(false);
    Swal.fire({
      title: t(`alert.confirm_delete_title`),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.deleteconfirm`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);

        FetchData('DELETE', `/${mod}/${attr.id}`, app.token, {}).then((result) => {
          setLoading(false);

          if (result.status === 'ok') {
            view(find);
            showNotify(t(`alert.deletecomplete`), 'success');
          } else {
            showNotify(t(`alert.deleteerror`), 'error');
          }
        });
      }
    });
  };

  const renderLine1 = (row) => {
    return (
      <button type='button' className='button' onClick={() => open(row)}>
        <div className='box'>
          <div className='box-header'>
            <div className='box-header-logo'>
              <img src={parent.logo} height='54' width='54' alt='' />
            </div>
            <div className='box-header-title'>{row.name}</div>
          </div>
          <div className='box-detail'>
            <div className='box-img'>
              <div>
                <img src={row.image} height='55' width='55' alt='' />
              </div>
              <div className='box-img-qrcode'>
                <img src={row.qrcode} width='55' height='55' alt='' />
              </div>
            </div>
            <div className='box-price'>
              <div className='box-price-product'>
                {Func.numberDisplay(row.line1_price)} <span className='baht'>.-</span>
              </div>
            </div>
          </div>
          <div className='clear'>&nbsp;</div>
        </div>
      </button>
    );
  };

  const renderLine2 = (row) => {
    return (
      <button type='button' className='button' onClick={() => open(row)}>
        <div className='box'>
          <div className='box-header'>
            <div className='box-header-logo'>
              <img src={parent.logo} height='54' width='54' alt='' />
            </div>
            <div className='box-header-title'>{row.name}</div>
          </div>
          <div className='box-detail'>
            <div className='box-img'>
              <div>
                <img src={row.image} height='55' width='55' alt='' />
              </div>
              <div className='box-img-qrcode'>
                <img src={row.qrcode} width='55' height='55' alt='' />
              </div>
            </div>
            <div className='box-price'>
              <div className='box-price-product-1'>
                {Func.numberDisplay(row.line1_quantity)} = {Func.numberDisplay(row.line1_price)}{' '}
                <span className='baht'>.-</span>
              </div>
              <div
                className={row.line2_type === 'D' ? 'box-price-product-2d' : 'box-price-product-2'}
              >
                {Func.numberDisplay(row.line2_quantity)} = {Func.numberDisplay(row.line2_price)}{' '}
                <span className='baht'>.-</span>
              </div>
            </div>
          </div>
          <div className='clear'>&nbsp;</div>
        </div>
      </button>
    );
  };

  const renderLine3 = (row) => {
    return (
      <button type='button' className='button' onClick={() => open(row)}>
        <div className='box'>
          <div className='box-header'>
            <div className='box-header-logo'>
              <img src={parent.logo} height='54' width='54' alt='' />
            </div>
            <div className='box-header-title'>{row.name}</div>
          </div>
          <div className='box-detail'>
            <div className='box-img'>
              <div>
                <img src={row.image} height='55' width='55' alt='' />
              </div>
              <div className='box-img-qrcode'>
                <img src={row.qrcode} width='55' height='55' alt='' />
              </div>
            </div>
            <div className='box-price'>
              <div className='box-price'>
                <div className='box-price-product-11'>
                  {Func.numberDisplay(row.line1_quantity)} = {Func.numberDisplay(row.line1_price)}{' '}
                  <span className='baht'>.-</span>
                </div>
                <div
                  className={
                    row.line2_type === 'D' ? 'box-price-product-22d' : 'box-price-product-22'
                  }
                >
                  {Func.numberDisplay(row.line2_quantity)} = {Func.numberDisplay(row.line2_price)}{' '}
                  <span className='baht'>.-</span>
                </div>
                <div
                  className={
                    row.line3_type === 'D' ? 'box-price-product-33d' : 'box-price-product-33'
                  }
                >
                  {Func.numberDisplay(row.line3_quantity)} = {Func.numberDisplay(row.line3_price)}{' '}
                  <span className='baht'>.-</span>
                </div>
              </div>
            </div>
          </div>
          <div className='clear'>&nbsp;</div>
        </div>
      </button>
    );
  };

  const renderList = () => {
    return (
      <MDBox>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MDBox display='flex' justifyContent='end' gap={1} p={2}>
              <TagProduct add={add} />
              <TagProductLocation add={add} />
            </MDBox>
          </Grid>
          <Grid item xs={12}>
            <MDBox p={2}>
              <Grid container spacing={1}>
                {record.map((row) => (
                  <Grid item xs={12} sm={12} md={6} lg={4} xl={4} key={row.id}>
                    {row.line * 1 === 1
                      ? renderLine1(row)
                      : row.line * 1 === 2
                      ? renderLine2(row)
                      : renderLine3(row)}
                  </Grid>
                ))}
              </Grid>
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderInfo = () => {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <MDInput
            label='ชื่อสินค้า'
            onChange={(e) => setAttr({ ...attr, name: e.target.value })}
            value={attr.name}
            variant='standard'
            fullWidth
            multiline
            inputProps={{
              autoComplete: 'off',
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <FormControl variant='standard' fullWidth>
            <InputLabel>จำนวนแถว</InputLabel>
            <Select
              fullWidth
              value={attr.line}
              variant='standard'
              onChange={(e) => setAttr({ ...attr, line: e.target.value })}
            >
              <MenuItem value='1'>1</MenuItem>
              <MenuItem value='2'>2</MenuItem>
              <MenuItem value='3'>3</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl variant='standard' fullWidth>
                <InputLabel>แถว 1</InputLabel>
                <Select
                  fullWidth
                  value={attr.line1}
                  variant='standard'
                  onChange={(e) => setAttr({ ...attr, line1: e.target.value })}
                >
                  <MenuItem value=''>-</MenuItem>
                  {cboCampaign.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {Func.numberDisplay(item.quantity)} = {Func.numberDisplay(item.price)} :{' '}
                      {item.code}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {attr.line > 1 ? (
              <Grid item xs={12}>
                <FormControl variant='standard' fullWidth>
                  <InputLabel>แถว 2</InputLabel>
                  <Select
                    fullWidth
                    value={attr.line2}
                    variant='standard'
                    onChange={(e) => setAttr({ ...attr, line2: e.target.value })}
                  >
                    <MenuItem value=''>-</MenuItem>
                    {cboCampaign.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {Func.numberDisplay(item.quantity)} = {Func.numberDisplay(item.price)} :{' '}
                        {item.code}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            ) : null}
            {attr.line > 2 ? (
              <Grid item xs={12}>
                <FormControl variant='standard' fullWidth>
                  <InputLabel>แถว 3</InputLabel>
                  <Select
                    fullWidth
                    value={attr.line3}
                    variant='standard'
                    onChange={(e) => setAttr({ ...attr, line3: e.target.value })}
                  >
                    <MenuItem value=''>-</MenuItem>
                    {cboCampaign.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {Func.numberDisplay(item.quantity)} = {Func.numberDisplay(item.price)} :{' '}
                        {item.code}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            ) : null}
          </Grid>
        </Grid>
      </Grid>
    );
  };

  const renderEdit = () => {
    return (
      <Dialog open={modalEdit} maxWidth='xs' keepMounted aria-describedby='dialog-edit' fullWidth>
        <DialogTitle>
          {t(`element.edit`)} #{attr.id}
          <IconButton
            aria-label='close'
            onClick={() => setModalEdit(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ minHeight: 280 }}>
          <MDBox p={1}>{renderInfo()}</MDBox>
        </DialogContent>
        <DialogActions>
          {confirm ? (
            <Grid container>
              <Grid item xs={6}>
                <MDButton variant='text' color='error' onClick={() => setConfirm(false)}>
                  <Icon>cancel</Icon>&nbsp;{t(`element.cancel`)}
                </MDButton>
              </Grid>
              <Grid item xs={6} display='flex' justifyContent='flex-end'>
                <MDButton variant='outlined' color='success' onClick={edit}>
                  <Icon>save</Icon>&nbsp;{t(`element.saveconfirm`)}
                </MDButton>
              </Grid>
            </Grid>
          ) : (
            <Grid container>
              <Grid item xs={6}>
                <MDButton variant='text' color='error' onClick={() => del()}>
                  <Icon>delete</Icon> {t(`element.delete`)}
                </MDButton>
              </Grid>
              <Grid item xs={6} display='flex' justifyContent='flex-end'>
                <MDButton variant='text' color='success' onClick={editConfirm}>
                  <Icon>save</Icon>&nbsp;{t(`element.save`)}
                </MDButton>
              </Grid>
            </Grid>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    view(find);
  }, [id]);

  return (
    <div>
      {renderList()}
      {renderEdit()}
      {notify}
    </div>
  );
}

export default TagList;
